import { useMemo } from "react";
import {
  Background,
  Controls,
  MarkerType,
  MiniMap,
  ReactFlow,
  type Edge,
  type Node,
} from "@xyflow/react";
import { statusDefinitions } from "../lib/status";
import type { Project, WorkItem, WorkItemDependency } from "../types/domain";

export function GraphView({
  project,
  items,
  dependencies,
}: {
  project: Project;
  items: WorkItem[];
  dependencies: WorkItemDependency[];
}) {
  const { nodes, edges } = useMemo(() => {
    const visible = items.filter((item) => item.itemType === "phase" || item.itemType === "block");
    const phases = visible
      .filter((item) => item.itemType === "phase")
      .sort((a, b) => a.sortOrder - b.sortOrder);
    const nodeList: Node[] = [];
    const edgeList: Edge[] = [];

    phases.forEach((phase, phaseIndex) => {
      const blocks = visible
        .filter((item) => item.parentId === phase.id)
        .sort((a, b) => a.sortOrder - b.sortOrder);
      const x = phaseIndex * 330;
      nodeList.push({
        id: phase.id,
        position: { x, y: 0 },
        data: { label: phase.title },
        style: {
          border: `2px solid ${project.accent}`,
          background: "#0d2633",
          color: "#e9fbff",
          borderRadius: 18,
          padding: 14,
          width: 245,
          fontWeight: 700,
        },
      });
      blocks.forEach((block, blockIndex) => {
        const tone = statusDefinitions[block.status].tone;
        nodeList.push({
          id: block.id,
          position: { x, y: 150 + blockIndex * 125 },
          data: { label: `${block.title}\n${statusDefinitions[block.status].shortLabel}` },
          style: {
            border: "1px solid #285264",
            background: block.isCurrentFocus ? "#143948" : "#0a202b",
            color: "#d7edf4",
            borderRadius: 14,
            padding: 12,
            width: 245,
            whiteSpace: "pre-line",
            boxShadow: tone === "active" ? `0 0 0 2px ${project.accent}55` : "none",
          },
        });
        edgeList.push({
          id: `parent-${phase.id}-${block.id}`,
          source: phase.id,
          target: block.id,
          animated: block.isCurrentFocus,
          style: { stroke: project.accent },
          markerEnd: { type: MarkerType.ArrowClosed, color: project.accent },
        });
      });
    });

    dependencies
      .filter(
        (dependency) =>
          visible.some((item) => item.id === dependency.workItemId) &&
          visible.some((item) => item.id === dependency.dependsOnWorkItemId),
      )
      .forEach((dependency) => {
        edgeList.push({
          id: `dependency-${dependency.workItemId}-${dependency.dependsOnWorkItemId}`,
          source: dependency.dependsOnWorkItemId,
          target: dependency.workItemId,
          type: "smoothstep",
          label: dependency.dependencyType === "blocks" ? "bloquea" : "relaciona",
          style: { stroke: "#f7b955", strokeDasharray: "5 5" },
          markerEnd: { type: MarkerType.ArrowClosed, color: "#f7b955" },
        });
      });
    return { nodes: nodeList, edges: edgeList };
  }, [dependencies, items, project.accent]);

  return (
    <div className="view-stack">
      <section className="section-toolbar">
        <div>
          <span className="eyebrow">Mapa navegable</span>
          <h2>Arquitectura de trabajo</h2>
          <p>
            Relaciones jerárquicas y dependencias explícitas. Arrastra, amplía y recorre el grafo.
          </p>
        </div>
      </section>
      <section className="panel graph-panel">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          fitView
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable
          minZoom={0.2}
          maxZoom={1.8}
        >
          <Background color="#234655" gap={28} size={1} />
          <MiniMap nodeColor={project.accent} maskColor="rgba(3, 13, 20, .76)" />
          <Controls />
        </ReactFlow>
      </section>
    </div>
  );
}
